import { Component, Event, EventEmitter, h, Prop, State, Element } from '@stencil/core';

/**
 * Default validation copy, used when neither the form handler nor the page supplied text.
 *
 * A page overrides per field with `required-message` / `invalid-message` (see the example below);
 * that is the only channel a block's frame schema can actually carry, since it pins the exact props
 * it uses with `additionalProperties: false`. Adding a new prop instead would need every template
 * schema regenerated, and until then no value would ever arrive.
 */
const MESSAGE_FIELD_REQUIRED = 'This field is required';
const MESSAGE_EMAIL_REQUIRED = 'Please enter your Email';
const MESSAGE_VALUE_INVALID = 'Invalid value';
const MESSAGE_EMAIL_INVALID = 'This value seems to be invalid';
const MESSAGE_FIELD_INVALID = 'This field is invalid';

/**
 * Input component with validation and error handling capabilities.
 * Error text is normally owned by the parent form handler, which writes it into `errorMessage`.
 * A page overrides it per field with `required-message` / `invalid-message` — the form handler
 * reads those when building validation errors, and this component falls back to them when it is
 * marked invalid without a message.
 *
 * @example
 *   <me-input
 *     label="Email"
 *     name="email"
 *     type="email"
 *     placeholder="Enter your email"
 *     required
 *     required-message="Please enter your email"
 *     invalid-message="Please enter a valid email address"
 *   ></me-input>
 */
@Component({
  tag: 'me-input',
  styleUrl: 'index.scss',
  shadow: false,
})
export class Input {
  @Prop({ reflect: true }) name: string = '';
  @Prop() placeholder: string = '';
  @Prop({ mutable: true, reflect: true }) value: string = '';
  @Prop() type: string = 'text';
  @Prop() disabled: boolean = false;
  @Prop() required: boolean = false;
  @Prop() label?: string;

  /**
   * Custom message for required error
   */
  @Prop() requiredMessage?: string;

  /**
   * Custom message for invalid error
   */
  @Prop() invalidMessage?: string;

  /**
   * Error type: 'required' | 'invalid' (set by form handler)
   */
  @Prop({ mutable: true, reflect: true }) errorType?: string;

  /**
   * Error message for input (set by form handler)
   */
  @Prop({ mutable: true, reflect: true }) errorMessage?: string;

  /**
   * Minimum length for input value
   */
  @Prop() minLength?: number;

  /**
   * Maximum length for input value
   */
  @Prop() maxLength?: number;

  /**
   * Custom message for minLength error
   */
  @Prop() minLengthMessage?: string;

  /**
   * Custom message for maxLength error
   */
  @Prop() maxLengthMessage?: string;

  @Prop({ mutable: true, reflect: true }) error?: string;

  @State() internalValue: string = '';

  @Event() valueInput: EventEmitter<string>;
  @Event() valueChange: EventEmitter<string>;
  @Event() clearFieldError: EventEmitter<string>;

  @Element() hostEl!: HTMLElement;

  componentWillLoad() {
    this.internalValue = this.value;
  }

  render() {
    const hasError = this.error === 'true';
    let inputId: string | undefined = undefined;
    if (this.label && this.name) {
      inputId = this.getResolvedName();
    }

    // The form handler normally writes the text into `errorMessage`. When the field is marked
    // invalid without one (a standalone `me-input`, or a handler that only set `error`), fall back
    // to the per-instance override and then to the default copy below.
    let errorText = this.errorMessage;
    if (!errorText && hasError) {
      const isEmailField = this.type === 'email';

      if (this.errorType === 'required') {
        errorText = this.requiredMessage || (isEmailField ? MESSAGE_EMAIL_REQUIRED : MESSAGE_FIELD_REQUIRED);
      } else if (this.errorType === 'invalid') {
        errorText = this.invalidMessage || (isEmailField ? MESSAGE_EMAIL_INVALID : MESSAGE_VALUE_INVALID);
      } else {
        errorText = MESSAGE_FIELD_INVALID;
      }
    }

    const resolvedName = this.getResolvedName();

    return (
      <div class={{ 'input-wrapper': true, 'has-error': hasError }}>
        {this.label && (
          <label htmlFor={inputId} class="input__label">
            {this.label}
          </label>
        )}
        <input
          class={{
            'input': true,
            'input--disabled': this.disabled,
            'input--error': hasError,
          }}
          {...(inputId ? { id: inputId } : {})}
          type={this.type}
          name={resolvedName}
          value={this.internalValue}
          placeholder={this.placeholder}
          disabled={this.disabled}
          required={this.required}
          minLength={this.minLength}
          maxLength={this.maxLength}
          onInput={this.handleInput}
          onChange={this.handleChange}
          onClick={this.handleClick}
          onFocus={this.handleFocus}
          autocomplete="off"
        />
        <me-text variant="small" class={`form-error-message ${hasError && errorText ? 'error-visible' : 'error-hidden'}`}>
          {errorText || '\u00A0'}
        </me-text>
      </div>
    );
  }

  private handleInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    this.internalValue = input.value;
    this.value = input.value;
    this.valueInput.emit(input.value);
    this.clearFieldError.emit(this.getResolvedName());
  };

  private handleChange = (event: Event) => {
    const input = event.target as HTMLInputElement;
    this.valueChange.emit(input.value);
    this.clearFieldError.emit(this.getResolvedName());
  };

  private handleClick = (event: MouseEvent) => {
    event.stopPropagation();
  };

  private handleFocus = () => {
    this.clearFieldError.emit(this.getResolvedName());
  };

  private getResolvedName(): string {
    // Find the nearest parent form with data-form-name attribute
    let parent = this.hostEl.parentElement;
    while (parent) {
      if (parent.tagName.toLowerCase() === 'form' && parent.hasAttribute('data-form-name')) {
        const formName = parent.getAttribute('data-form-name');
        if (formName && this.name) {
          return `${formName}-${this.name}`;
        }
      }
      parent = parent.parentElement;
    }
    return this.name;
  }
}
